/**
 * Utility functions for formatting data values
 */

/**
 * Format capacity in binary units (TiB, GiB, MiB, KiB)
 *
 * NetApp reports capacity in binary (base-2) units, not decimal (base-10).
 * - 1 TiB = 1,024 GiB = 1,024^3 bytes
 * - 1 TB = 1,000 GB = 1,000^3 bytes
 *
 * @param tib - Capacity value in TiB (tebibytes)
 * @param decimals - Number of decimal places (default: 2)
 * @param autoScale - Automatically scale to appropriate unit (default: true)
 * @returns Formatted string with unit (e.g., "1.50 TiB", "512 GiB")
 */
export function formatCapacity(
  tib: number | null | undefined | string,
  decimals: number = 2,
  autoScale: boolean = true
): string {
  // Convert to number if it's a string
  const numValue = typeof tib === 'string' ? parseFloat(tib) : tib;

  if (numValue === null || numValue === undefined || isNaN(numValue)) {
    return '0 TiB';
  }

  // Convert to absolute for comparison (but keep sign for display)
  const absTib = Math.abs(numValue);

  if (!autoScale) {
    return `${numValue.toFixed(decimals)} TiB`;
  }

  // Auto-scale to appropriate unit
  if (absTib >= 1024) {
    // PiB (pebibytes)
    return `${(numValue / 1024).toFixed(decimals)} PiB`;
  } else if (absTib >= 1) {
    // TiB (tebibytes)
    return `${numValue.toFixed(decimals)} TiB`;
  } else if (absTib >= 0.001) {
    // GiB (gibibytes)
    return `${(numValue * 1024).toFixed(decimals)} GiB`;
  } else {
    // MiB (mebibytes)
    return `${(numValue * 1024 * 1024).toFixed(decimals)} MiB`;
  }
}

/**
 * Format capacity as compact display (e.g., "1.5T", "512G")
 * Useful for charts and tight spaces
 *
 * @param tib - Capacity value in TiB
 * @param decimals - Number of decimal places (default: 1)
 * @returns Compact formatted string (e.g., "1.5T", "512G")
 */
export function formatCapacityCompact(
  tib: number | null | undefined | string,
  decimals: number = 1
): string {
  // Convert to number if it's a string
  const numValue = typeof tib === 'string' ? parseFloat(tib) : tib;

  if (numValue === null || numValue === undefined || isNaN(numValue)) {
    return '0T';
  }

  const absTib = Math.abs(numValue);

  if (absTib >= 1024) {
    return `${(numValue / 1024).toFixed(decimals)}P`;
  } else if (absTib >= 1) {
    return `${numValue.toFixed(decimals)}T`;
  } else if (absTib >= 0.001) {
    return `${(numValue * 1024).toFixed(decimals)}G`;
  } else {
    return `${(numValue * 1024 * 1024).toFixed(decimals)}M`;
  }
}

/**
 * Format percentage with specified decimal places
 *
 * @param value - Percentage value (0-100)
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted percentage string (e.g., "45.3%")
 */
export function formatPercentage(
  value: number | null | undefined | string,
  decimals: number = 1
): string {
  // Convert to number if it's a string
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (numValue === null || numValue === undefined || isNaN(numValue)) {
    return '0%';
  }
  return `${numValue.toFixed(decimals)}%`;
}

/**
 * Format efficiency ratio (e.g., "19.23:1")
 *
 * @param ratio - Efficiency ratio value
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted ratio string (e.g., "19.23:1")
 */
export function formatEfficiencyRatio(
  ratio: number | null | undefined | string,
  decimals: number = 2
): string {
  // Convert to number if it's a string
  const numValue = typeof ratio === 'string' ? parseFloat(ratio) : ratio;

  if (numValue === null || numValue === undefined || isNaN(numValue) || numValue < 1) {
    return 'N/A';
  }
  return `${numValue.toFixed(decimals)}:1`;
}

/**
 * Format number with thousands separator
 *
 * @param value - Number to format
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted number string (e.g., "1,234.56")
 */
export function formatNumber(
  value: number | null | undefined | string,
  decimals: number = 0
): string {
  // Convert to number if it's a string
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (numValue === null || numValue === undefined || isNaN(numValue)) {
    return '0';
  }
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(numValue);
}
